import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

// Hacer Pusher disponible globalmente (requerido por Laravel Echo)
(window as any).Pusher = Pusher;

export interface WebSocketMessage {
    channel: string;
    event: string;
    data: any;
    timestamp: Date;
}

export interface ConnectionStatus {
    connected: boolean;
    connecting: boolean;
    error: string | null;
}

const INITIAL_STATUS_VALUE: ConnectionStatus = {
    connected: false,
    connecting: false,
    error: null
};

@Injectable({
  providedIn: 'root'
})
export class WebsocketLaravelReverb {
  private _http = inject(HttpClient);
  
  private echo: any = null;
  private subscribedChannels: Map<string, any> = new Map();

  private _messages = signal<WebSocketMessage[]>([]);
  public  messages = this._messages.asReadonly();
  
  private _status = signal<ConnectionStatus>(INITIAL_STATUS_VALUE);
  public status = this._status.asReadonly();
  
  setMessages = (messages: WebSocketMessage[]) => {
      this._messages.set(messages);
  }
  
  setStatus = (status: ConnectionStatus) => {
      this._status.set(status);
  }

  /**
   * Conecta al servidor WebSocket de Laravel Reverb
   * 
   * @param customConfig - Configuración opcional para sobrescribir environment
   */
  connect(customConfig?: Partial<typeof environment.reverb>): void {
    if (this.echo) {
      console.warn('WebSocket ya conectado');
      return;
    }

    const config = { ...environment.reverb, ...customConfig };

    this.setStatus({
      connected: false,
      connecting: true,
      error: null
    });

    try {
      this.echo = new Echo({
        broadcaster: 'reverb',
        key: config.appKey,
        wsHost: config.host,
        wsPort: config.port,
        wssPort: config.port,
        forceTLS: config.scheme === 'wss',
        enabledTransports: ['ws', 'wss'],
        disableStats: true,
      });

      // Escuchar eventos de conexión
      if (this.echo?.connector?.pusher?.connection) {
        this.echo.connector.pusher.connection.bind('connected', () => {
          console.log('✅ WebSocket conectado a Laravel Reverb');
          this.setStatus({
            connected: true,
            connecting: false,
            error: null
          });
        });

        this.echo.connector.pusher.connection.bind('error', (error: any) => {
          console.error('❌ Error de WebSocket:', error);
          this.setStatus({
            connected: false,
            connecting: false,
            error: error?.message || 'Error de conexión'
          });
        });

        this.echo.connector.pusher.connection.bind('disconnected', () => {
          console.log('🔌 WebSocket desconectado');
          this.setStatus(INITIAL_STATUS_VALUE);
        });
      }

    } catch (error: any) {
      console.error('Error al inicializar Echo:', error);
      this.setStatus({
        connected: false,
        connecting: false,
        error: error?.message || 'Error al inicializar'
      });
    }
  }

  /**
   * Desconecta del servidor WebSocket
   */
  disconnect(): void {
    if (this.echo) {
      this.echo.disconnect();
      this.echo = null;
      this.subscribedChannels.clear();
      this.setStatus(INITIAL_STATUS_VALUE);
    }
  }

  /**
   * Suscribirse a un canal público y escuchar un evento específico
   * 
   * @param channelName - Nombre del canal (ej: 'chat', 'notifications')
   * @param eventName - Nombre del evento a escuchar (ej: 'MessageEvent')
   * @returns Observable que emite los datos del evento
   * 
   * @example
   * ```typescript
   * this.wsService.subscribeToChannel('chat', 'MessageEvent')
   *   .subscribe(data => {
   *     console.log('Nuevo mensaje:', data);
   *   });
   * ```
   */
  subscribeToChannel(channelName: string, eventName: string): Observable<any> {
    return new Observable(observer => {
      if (!this.echo) {
        observer.error(new Error('WebSocket no conectado. Llama a connect() primero.'));
        return;
      }

      const channel = this.echo.channel(channelName);
      this.subscribedChannels.set(channelName, channel);

      channel.listen(`.${eventName}`, (data: any) => {
        const message: WebSocketMessage = {
          channel: channelName,
          event: eventName,
          data,
          timestamp: new Date()
        };

        // Agregar al historial de mensajes
        const currentMessages = this._messages();
        this.setMessages([...currentMessages, message]);

        observer.next(data);
      });

      // Cleanup cuando se cancela la suscripción
      return () => {
        this.leaveChannel(channelName);
      };
    });
  }
  
    sendMessage(channelName: string, event: string, message?: unknown): void {
      let data: any;

      if (typeof message === 'string') {
        // Intenta parsear si es un JSON válido, si no, envía como string
        try {
          data = JSON.parse(message);
        } catch {
          data = message;
        }
      } else if (typeof message === 'number' || typeof message === 'boolean') {
        data = message;
      } else if (typeof message === 'object' && message !== null) {
        data = message;
      } else {
        alert('Error: Tipo de mensaje no soportado');
        return;
      }

      const payload = {
        channel: channelName,
        event: event,
        data: [data]
      };

      this._http.post(`${environment.apiUrlWebsocket}/broadcast`, payload)
        .subscribe({
          next: (response) => console.log('Mensaje enviado:', response),
          error: (error) => console.error('Error al enviar:', error)
        });
    }

  /**
   * Salir de un canal
   * 
   * @param channelName - Nombre del canal a abandonar
   */
  leaveChannel(channelName: string): void {
    if (this.echo) {
      this.echo.leave(channelName);
      this.subscribedChannels.delete(channelName);
    }
  }

  /**
   * Obtener los canales suscritos actualmente
   */
  getSubscribedChannels(): string[] {
    return Array.from(this.subscribedChannels.keys());
  }

  /**
   * Limpiar el historial de mensajes
   */
  clearMessages(): void {
    this.setMessages([]);
  }
}
